import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

import { Card } from '@/components/public/Section';
import { Button } from '@/components/ui/button';
import { CheckboxField, Field, FormError, SelectField, TextareaField } from '@/components/ui/field';
import { useBlurValidation } from '@/hooks/use-blur-validation';
import { api } from '@/lib/api';
import { CAREERS, NAP } from '@/lib/public-content';
import {
  DAY_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  EXPERIENCE_TYPE_OPTIONS,
  EXPERIENCE_YEARS_OPTIONS,
  careersApplicationSchema,
  type HiringPosition,
} from '@shared/schemas/careers';
import { HONEYPOT_FIELD, US_STATES } from '@shared/schemas/intake';

/*
 * The careers application — ROADMAP.md §8.7, rebuilt from
 * `reference/prototype-copy/careers-form.md` field by field.
 *
 * **"The form is the one thing that must not regress."** It is the only surface
 * in this project receiving anything real: people are applying for jobs through
 * it today. Every field the live form collects is here, including the four the
 * roadmap's own summary was short of.
 *
 * What is fixed on the way through, and all of it is live on the prototype now:
 *
 *   - **All 34 inputs have `name=null` and `id=null` there**, and no `<label
 *     for>` binds anywhere. Password managers cannot fill it and a screen
 *     reader reads unlabelled boxes. Every field here is built through
 *     `Field`/`SelectField`, which cannot render without both.
 *   - **Days Available and Types of Cleaning Experience are bare `<button>`
 *     groups** with no `role`, no `aria-pressed` and no name — a screen-reader
 *     user cannot tell they are multi-selects or what is chosen, and neither
 *     group submits anything a form parser can see. Real checkboxes in a
 *     `<fieldset>` with a `<legend>` here.
 *   - **Validation on blur**, not on keystroke.
 *   - Three fields the live form marks required *and* makes impossible — years
 *     of experience beside "I don't have Cleaning Experience", and the same for
 *     employers and references. Required only when the applicant says they have
 *     them; see the schema.
 *
 * P2's duplicate own-supplies checkbox is **not** present on the current build —
 * checked against the capture rather than assumed, and it renders once here.
 */

interface EmployerRow {
  employerName: string;
  jobTitle: string;
  datesWorked: string;
  responsibilities: string;
  reasonForLeaving: string;
}

interface ReferenceRow {
  name: string;
  phone: string;
  relationship: string;
}

const emptyEmployer = (): EmployerRow => ({
  employerName: '',
  jobTitle: '',
  datesWorked: '',
  responsibilities: '',
  reasonForLeaving: '',
});

const emptyReference = (): ReferenceRow => ({ name: '', phone: '', relationship: '' });

const STATE_OPTIONS = US_STATES.map((state) => ({ value: state, label: state }));

/** A titled block, matching the form's own five sections. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-5 border-t border-ink-300 pt-6">
      <legend className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-700">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

/** Trim a row to `undefined` where the schema expects an optional. */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

export function CareersForm() {
  const [renderedAt] = useState(() => Date.now());

  const positions = useQuery({
    queryKey: ['public', 'positions'],
    queryFn: () => api.get<{ items: HiringPosition[] }>('/api/public/intake/positions'),
  });

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [state, setState] = useState('');

  const [positionId, setPositionId] = useState('');
  const [desiredPay, setDesiredPay] = useState('');
  const [availableStartDate, setAvailableStartDate] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [daysAvailable, setDaysAvailable] = useState<string[]>([]);

  /*
   * **The form asks the negative; this holds the positive.**
   *
   * The checkbox below is labelled "I don't have Cleaning Experience", exactly
   * as the live form has it, and it is bound to `!hasCleaningExperience`. The
   * inversion happens here, once, at the edge — everything downstream says what
   * it means.
   */
  const [hasCleaningExperience, setHasCleaningExperience] = useState(true);
  const [experienceYears, setExperienceYears] = useState('');
  const [experienceTypes, setExperienceTypes] = useState<string[]>([]);
  const [hasOwnSupplies, setHasOwnSupplies] = useState(false);
  const [hasReliableTransportation, setHasReliableTransportation] = useState(false);

  const [hasWorkExperience, setHasWorkExperience] = useState(true);
  const [employers, setEmployers] = useState<EmployerRow[]>([emptyEmployer(), emptyEmployer()]);

  const [hasReferences, setHasReferences] = useState(true);
  const [references, setReferences] = useState<ReferenceRow[]>([
    emptyReference(),
    emptyReference(),
  ]);

  const [additionalInfo, setAdditionalInfo] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const values = useMemo(() => {
    // A row counts only when it names an employer. The second block is
    // optional, and a half-typed one should not become a validation error the
    // applicant cannot see the cause of.
    const filledEmployers = employers
      .filter((employer) => employer.employerName.trim() !== '')
      .map((employer) => ({
        employerName: employer.employerName,
        jobTitle: employer.jobTitle,
        datesWorked: optional(employer.datesWorked),
        responsibilities: optional(employer.responsibilities),
        reasonForLeaving: optional(employer.reasonForLeaving),
      }));

    const filledReferences = references
      .filter((reference) => reference.name.trim() !== '')
      .map((reference) => ({
        name: reference.name,
        phone: reference.phone,
        relationship: optional(reference.relationship),
      }));

    return {
      fullName,
      phone,
      email,
      address: optional(address),
      city: optional(city),
      postalCode: optional(postalCode),
      state: state === '' ? undefined : state,
      positionId,
      desiredPay: optional(desiredPay),
      availableStartDate: optional(availableStartDate),
      employmentType,
      daysAvailable,
      hasCleaningExperience,
      experienceYears: experienceYears === '' ? undefined : experienceYears,
      experienceTypes,
      hasOwnSupplies,
      hasReliableTransportation,
      hasWorkExperience,
      employers: hasWorkExperience ? filledEmployers : [],
      hasReferences,
      references: hasReferences ? filledReferences : [],
      additionalInfo: optional(additionalInfo),
      [HONEYPOT_FIELD]: honeypot,
      renderedAt,
    };
  }, [
    fullName,
    phone,
    email,
    address,
    city,
    postalCode,
    state,
    positionId,
    desiredPay,
    availableStartDate,
    employmentType,
    daysAvailable,
    hasCleaningExperience,
    experienceYears,
    experienceTypes,
    hasOwnSupplies,
    hasReliableTransportation,
    hasWorkExperience,
    employers,
    hasReferences,
    references,
    additionalInfo,
    honeypot,
    renderedAt,
  ]);

  const { errors, isValid, parsed, touch, touchAll } = useBlurValidation(
    careersApplicationSchema,
    values,
  );

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  function updateEmployer(index: number, patch: Partial<EmployerRow>) {
    setEmployers((current) =>
      current.map((row, position) => (position === index ? { ...row, ...patch } : row)),
    );
  }

  function updateReference(index: number, patch: Partial<ReferenceRow>) {
    setReferences((current) =>
      current.map((row, position) => (position === index ? { ...row, ...patch } : row)),
    );
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setFailure(null);
    touchAll();
    if (!isValid || parsed === null) return;

    setSubmitting(true);
    api
      .post('/api/public/intake/application', parsed)
      .then(() => {
        setSubmitted(true);
      })
      .catch((cause: unknown) => {
        setFailure(
          cause instanceof Error && cause.message.includes('no longer open')
            ? cause.message
            : `Something went wrong sending that. Please try again, or ring us on ${NAP.phone}.`,
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  if (submitted) {
    return (
      <Card>
        <div className="flex gap-3">
          <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-status-completed-text" aria-hidden />
          <div>
            <h2 aria-live="polite" className="text-h2">
              Thanks for applying
            </h2>
            <p className="mt-2 text-body text-ink-700">
              We have sent a confirmation to <strong>{email}</strong>. We read every application
              ourselves, so it takes a few days rather than a few minutes — and if we would like to
              talk, we will ring the number you gave us.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const positionOptions = (positions.data?.items ?? []).map((row) => ({
    value: row.id,
    label: row.name,
  }));

  return (
    <Card>
      <h2 className="text-h2">Apply</h2>
      <p className="mt-1 text-meta text-ink-500">{CAREERS.requiredNote}</p>

      <form onSubmit={submit} className="mt-6 space-y-8" noValidate>
        <FormError>{failure}</FormError>

        {/* Hidden from sight and from assistive technology — see QuoteForm. */}
        <div aria-hidden="true" className="hidden">
          <label htmlFor="careers-company-website">Company website</label>
          <input
            id="careers-company-website"
            name={HONEYPOT_FIELD}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(event) => {
              setHoneypot(event.target.value);
            }}
          />
        </div>

        <Section title="Applicant Information">
          <Field
            label="Full Name"
            name="fullName"
            required
            autoComplete="name"
            placeholder="Jane Smith"
            value={fullName}
            error={errors.fullName}
            onChange={(event) => {
              setFullName(event.target.value);
            }}
            onBlur={() => {
              touch('fullName');
            }}
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Phone Number"
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              placeholder="(435) 000-0000"
              value={phone}
              error={errors.phone}
              onChange={(event) => {
                setPhone(event.target.value);
              }}
              onBlur={() => {
                touch('phone');
              }}
            />
            <Field
              label="Email Address"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="jane@email.com"
              value={email}
              error={errors.email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              onBlur={() => {
                touch('email');
              }}
            />
          </div>

          <Field
            label="Current Address"
            name="address"
            autoComplete="street-address"
            placeholder="123 Main St"
            value={address}
            error={errors.address}
            onChange={(event) => {
              setAddress(event.target.value);
            }}
            onBlur={() => {
              touch('address');
            }}
          />

          <div className="grid gap-5 sm:grid-cols-3">
            <Field
              label="City"
              name="city"
              autoComplete="address-level2"
              placeholder="Cedar City"
              value={city}
              error={errors.city}
              onChange={(event) => {
                setCity(event.target.value);
              }}
              onBlur={() => {
                touch('city');
              }}
            />
            <Field
              label="Zip"
              name="postalCode"
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="84720"
              value={postalCode}
              error={errors.postalCode}
              onChange={(event) => {
                setPostalCode(event.target.value);
              }}
              onBlur={() => {
                touch('postalCode');
              }}
            />
            <SelectField
              label="State"
              name="state"
              autoComplete="address-level1"
              // Blank with no default, as the live form has it — an applicant
              // may well be moving here from somewhere else.
              placeholder="Select"
              options={STATE_OPTIONS}
              value={state}
              error={errors.state}
              onChange={(event) => {
                setState(event.target.value);
              }}
            />
          </div>
        </Section>

        <Section title="Position Information">
          <SelectField
            label="Position Applying For"
            name="positionId"
            required
            placeholder={positions.isPending ? 'Loading…' : 'Select a position'}
            options={positionOptions}
            value={positionId}
            error={errors.positionId}
            onChange={(event) => {
              setPositionId(event.target.value);
            }}
            onBlur={() => {
              touch('positionId');
            }}
          />
          {positions.isError ? (
            // A failed request is an error, never an empty state (P22). A
            // silently empty dropdown reads as "they are not hiring".
            <FormError>
              We could not load the list of jobs. Please refresh, or ring us on {NAP.phone}.
            </FormError>
          ) : null}

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="Desired Pay"
              name="desiredPay"
              placeholder="e.g. $18/hr or $20–22/hr"
              value={desiredPay}
              error={errors.desiredPay}
              onChange={(event) => {
                setDesiredPay(event.target.value);
              }}
              onBlur={() => {
                touch('desiredPay');
              }}
            />
            <Field
              label="Available Start Date"
              name="availableStartDate"
              type="date"
              value={availableStartDate}
              error={errors.availableStartDate}
              onChange={(event) => {
                setAvailableStartDate(event.target.value);
              }}
              onBlur={() => {
                touch('availableStartDate');
              }}
            />
          </div>

          <SelectField
            label="Full-Time / Part-Time"
            name="employmentType"
            required
            placeholder="Select"
            options={EMPLOYMENT_TYPE_OPTIONS.map((option) => ({ ...option }))}
            value={employmentType}
            error={errors.employmentType}
            onChange={(event) => {
              setEmploymentType(event.target.value);
            }}
            onBlur={() => {
              touch('employmentType');
            }}
          />

          {/*
            **Days Available** — real checkboxes in a fieldset with a legend.
            On the live form this is seven bare `<button>` elements with no
            name, no role and no pressed state: a screen-reader user cannot
            tell it is a multi-select, cannot tell what is chosen, and nothing
            is submitted a form parser could see.

            It decides whether somebody can cover a Saturday, which is why it
            has a column now.
          */}
          <fieldset>
            <legend className="text-label text-ink-900">Days Available</legend>
            <div className="mt-1 grid grid-cols-2 gap-x-4 sm:grid-cols-4">
              {DAY_OPTIONS.map((day) => (
                <CheckboxField
                  key={day.value}
                  name="daysAvailable"
                  value={day.value}
                  label={day.label}
                  checked={daysAvailable.includes(day.value)}
                  onCheckedChange={() => {
                    setDaysAvailable((current) => toggle(current, day.value));
                  }}
                />
              ))}
            </div>
          </fieldset>

          <div className="space-y-1">
            <CheckboxField
              name="noCleaningExperience"
              // The live form's own wording, kept — and bound to the inverse of
              // the field it sets, so the stored column says what it means.
              label="I don't have Cleaning Experience"
              checked={!hasCleaningExperience}
              onCheckedChange={(checked) => {
                setHasCleaningExperience(!checked);
              }}
            />

            {hasCleaningExperience ? (
              <SelectField
                label="Years of Cleaning Experience"
                name="experienceYears"
                required
                placeholder="Select"
                options={EXPERIENCE_YEARS_OPTIONS.map((option) => ({ ...option }))}
                value={experienceYears}
                error={errors.experienceYears}
                onChange={(event) => {
                  setExperienceYears(event.target.value);
                }}
                onBlur={() => {
                  touch('experienceYears');
                }}
              />
            ) : null}
          </div>

          {/* **Types of Cleaning Experience** — the second bare-button group,
              rebuilt the same way. It is what makes an applicant a Commercial
              Cleaner rather than a House Cleaner. */}
          <fieldset>
            <legend className="text-label text-ink-900">Types of Cleaning Experience</legend>
            <div className="mt-1 grid gap-x-4 sm:grid-cols-2">
              {EXPERIENCE_TYPE_OPTIONS.map((type) => (
                <CheckboxField
                  key={type.value}
                  name="experienceTypes"
                  value={type.value}
                  label={type.label}
                  checked={experienceTypes.includes(type.value)}
                  onCheckedChange={() => {
                    setExperienceTypes((current) => toggle(current, type.value));
                  }}
                />
              ))}
            </div>
          </fieldset>

          <CheckboxField
            name="hasOwnSupplies"
            label="I have my own cleaning supplies / equipment"
            checked={hasOwnSupplies}
            onCheckedChange={setHasOwnSupplies}
          />

          <CheckboxField
            name="hasReliableTransportation"
            label="I have reliable transportation"
            checked={hasReliableTransportation}
            onCheckedChange={setHasReliableTransportation}
          />
        </Section>

        <Section title="Work Experience">
          <CheckboxField
            name="noWorkExperience"
            label="I don't have Work Experience"
            checked={!hasWorkExperience}
            onCheckedChange={(checked) => {
              setHasWorkExperience(!checked);
            }}
          />

          {hasWorkExperience ? (
            <>
              {errors.employers ? <FormError>{errors.employers}</FormError> : null}
              {employers.map((employer, index) => (
                <fieldset key={index} className="space-y-4 rounded-md border border-ink-300 p-4">
                  <legend className="px-1.5 text-label text-bark-700">
                    {index === 0 ? 'Most Recent Employer' : 'Previous Employer'}
                    {index === 1 ? <span className="text-ink-500"> (optional)</span> : null}
                  </legend>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="Employer Name"
                      name={`employers.${String(index)}.employerName`}
                      required={index === 0}
                      value={employer.employerName}
                      error={errors[`employers.${String(index)}.employerName`]}
                      onChange={(event) => {
                        updateEmployer(index, { employerName: event.target.value });
                      }}
                      onBlur={() => {
                        touch(`employers.${String(index)}.employerName`);
                      }}
                    />
                    <Field
                      label="Job Title"
                      name={`employers.${String(index)}.jobTitle`}
                      required={index === 0}
                      value={employer.jobTitle}
                      error={errors[`employers.${String(index)}.jobTitle`]}
                      onChange={(event) => {
                        updateEmployer(index, { jobTitle: event.target.value });
                      }}
                      onBlur={() => {
                        touch(`employers.${String(index)}.jobTitle`);
                      }}
                    />
                  </div>

                  <Field
                    label="Dates Worked"
                    name={`employers.${String(index)}.datesWorked`}
                    placeholder="e.g. Jan 2022 – Mar 2024"
                    value={employer.datesWorked}
                    onChange={(event) => {
                      updateEmployer(index, { datesWorked: event.target.value });
                    }}
                  />

                  <TextareaField
                    label="Responsibilities"
                    name={`employers.${String(index)}.responsibilities`}
                    rows={3}
                    value={employer.responsibilities}
                    onChange={(event) => {
                      updateEmployer(index, { responsibilities: event.target.value });
                    }}
                  />

                  <Field
                    label="Reason for Leaving"
                    name={`employers.${String(index)}.reasonForLeaving`}
                    value={employer.reasonForLeaving}
                    onChange={(event) => {
                      updateEmployer(index, { reasonForLeaving: event.target.value });
                    }}
                  />
                </fieldset>
              ))}
            </>
          ) : null}
        </Section>

        <Section title="References">
          <p className="text-body text-ink-700">Please do not list family members.</p>

          <CheckboxField
            name="noReferences"
            label="I don't have References"
            checked={!hasReferences}
            onCheckedChange={(checked) => {
              setHasReferences(!checked);
            }}
          />

          {hasReferences ? (
            <>
              {errors.references ? <FormError>{errors.references}</FormError> : null}
              {references.map((reference, index) => (
                <fieldset key={index} className="space-y-4 rounded-md border border-ink-300 p-4">
                  <legend className="px-1.5 text-label text-bark-700">
                    Reference {index + 1}
                    {index === 1 ? <span className="text-ink-500"> (optional)</span> : null}
                  </legend>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field
                      label="Name"
                      name={`references.${String(index)}.name`}
                      required={index === 0}
                      value={reference.name}
                      error={errors[`references.${String(index)}.name`]}
                      onChange={(event) => {
                        updateReference(index, { name: event.target.value });
                      }}
                      onBlur={() => {
                        touch(`references.${String(index)}.name`);
                      }}
                    />
                    <Field
                      label="Phone"
                      name={`references.${String(index)}.phone`}
                      type="tel"
                      required={index === 0}
                      placeholder="(435) 000-0000"
                      value={reference.phone}
                      error={errors[`references.${String(index)}.phone`]}
                      onChange={(event) => {
                        updateReference(index, { phone: event.target.value });
                      }}
                      onBlur={() => {
                        touch(`references.${String(index)}.phone`);
                      }}
                    />
                    <Field
                      label="Relationship"
                      name={`references.${String(index)}.relationship`}
                      placeholder={index === 0 ? 'e.g. Former Supervisor' : 'e.g. Coworker'}
                      value={reference.relationship}
                      onChange={(event) => {
                        updateReference(index, { relationship: event.target.value });
                      }}
                    />
                  </div>
                </fieldset>
              ))}
            </>
          ) : null}
        </Section>

        <Section title="Additional Information">
          <TextareaField
            label="Is there anything else you'd like us to know?"
            name="additionalInfo"
            rows={4}
            placeholder="Any additional skills, certifications, or information…"
            value={additionalInfo}
            error={errors.additionalInfo}
            onChange={(event) => {
              setAdditionalInfo(event.target.value);
            }}
            onBlur={() => {
              touch('additionalInfo');
            }}
          />
        </Section>

        <Button type="submit" size="lg" disabled={submitting}>
          {submitting ? 'Sending…' : 'Submit Application'}
        </Button>
      </form>
    </Card>
  );
}
